import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import jsQR from 'jsqr';
import Common from '../Common/common';
import moment from 'moment';

const videoWidth = 600;
const videoHeight = 600;
const videoFrameRate = 10;

const constraints = {
  audio: false,
  video: {
    width: { ideal: videoWidth },
    height: { ideal: videoHeight },
    frameRate: {
      max: videoFrameRate,
    },
    facingMode: 'environment',
    focusMode: 'continuous',
  },
};

const QRCodeScanner = ({ match, setMode, setQrCodeData, scanning, message, appState, cardId }) => {
  const history = useHistory();
  const videoRef = useRef(null);
  const intervalRef = useRef();
  const canvasRef = useRef(null);
  const [isContinue, setIsContinue] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const streamRef = useRef(null);
  const siteId = match.params.siteId;
  const mallId = match.params.mallId;

  const scanQrExternal = useCallback(() => {
    if (window.liff.isInClient()) {
      window.open(`${process.env.REACT_APP_FRONTEND_URL}/${siteId}/qr?openExternalBrowser=1&timestamp=${moment().format('YYYYMMDDHHmmSS')}`, '_blank');
    } else {
      history.push(`/${siteId}/qr`);
    }
  }, [history, siteId]);

  const stopCamera = () => {
    setErrorMessage(null);
    if (videoRef.current) {
    videoRef.current.pause();
    videoRef.current.src = '';
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
  };
  const openCamera = useCallback(async () => {
    try {
      if (!appState.mall_id || !appState.shop_id) {
        setErrorMessage('店舗が特定できませんでした。\nログインしなおしてください。');
      } else if (navigator.mediaDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInputDevices = devices.filter(device => device.kind === 'videoinput');

        if (videoInputDevices.length === 0) {
          setErrorMessage('カメラが見つかりませんでした。');
          return;
        }
        const video = videoRef.current;
  
        if (video) {
          try {
            const stream = await navigator.mediaDevices.getUserMedia(constraints);

            if (stream.active) {
              const lastReload = localStorage.getItem('lastReloadDate');
              const now = new Date();
              const hours = now.getHours();
              const today = moment(now, 'YYYYMMDD').format('YYYY-MM-DD')
              if ((hours >= 3 && lastReload !== today) || !lastReload) {
                localStorage.setItem('lastReloadDate', today);
                window.location.href = `/${siteId}/${mallId}/backyard/`;
                //window.location.reload(true);
              }
              streamRef.current = stream;
              video.srcObject = stream;
              setIsContinue(true);
            } else {
              throw new Error('Stream is not active');
            }
          } catch (err) {
            if (err.name === 'NotAllowedError') {
              setErrorMessage('カメラへのアクセスが拒否されました。ブラウザの設定を確認してください。');
            } else if (err.name === 'NotFoundError') {
              setErrorMessage('カメラが見つかりませんでした。');
            } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
              setErrorMessage('カメラが他のアプリケーションで使用中です。');
            } else {
              setErrorMessage('カメラのストリーム取得に失敗しました。再試行します。');
            }
            const retryStream = await navigator.mediaDevices.getUserMedia(constraints);
            if (retryStream.active) {
              streamRef.current = retryStream;
              video.srcObject = retryStream;
              setIsContinue(true);
            } else {
              throw new Error('Stream is not active');
            }
          }
        } else {
          setErrorMessage('画面を更新してください。');
        }
      } else {
        console.log('mediaDevices not found');
        if (navigator.getUserMedia) {
          console.log('getUserMedia found');
          const video = videoRef.current;
          if (video) {
            const successFunc = (stream) => {
              streamRef.current = stream;
              video.srcObject = stream;
              setIsContinue(true);
            };
            const errorFunc = (err) => {
              console.log(err);
              setErrorMessage('二次元コード読取が起動できませんでした。(2)');
            }
            navigator.getUserMedia(constraints, successFunc, errorFunc);
          }
        } else {
          console.log('getUserMedia not found');
          setErrorMessage('ブラウザを起動します。');
          scanQrExternal();
        }
      }
    } catch (err) {
      console.log(err);
      setErrorMessage('カメラを起動できませんでした。\nカメラ付き端末でご利用ください。');
    }
  }, [appState, scanQrExternal, mallId, siteId]);

  useEffect(() => {
    openCamera();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        openCamera();
      } else {
        stopCamera();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopCamera();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      setIsContinue(false);
    };
  }, [openCamera]);

  useEffect(() => {
    if (!isContinue) {
      return;
    }

    const decodeQRCode = () => {
      const context = canvasRef ? (canvasRef.current ? canvasRef.current.getContext('2d') : undefined) : undefined;
      const video = videoRef ? videoRef.current : undefined;

      if (!context || !video) {
        return;
      }

      context.drawImage(video, 0, 0, videoWidth, videoHeight);
      const imageData = context.getImageData(0, 0, videoWidth, videoHeight);
      const code = jsQR(imageData.data, videoWidth, videoHeight);

      return code ? code.data : undefined;
    };

    const intervalId = window.setInterval(() => {
      if (scanning) {
        const decodedValue = decodeQRCode();

        if (!decodedValue) {
          return;
        }
        console.log(decodedValue);
        setQrCodeData(decodedValue);
      }
    }, 1000 / videoFrameRate);
    intervalRef.current = intervalId;

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [isContinue, setQrCodeData, scanning]);

  useEffect(() => {
    if (!scanning) {
      stopCamera();
    }
  }, [scanning]);
  
  useEffect(() => {
    if (cardId) {
      setQrCodeData(null);
    }
  }, [cardId, setQrCodeData]);

  return (
    <div className="d-flex flex-column align-items-center">
      <div className="row bg-white d-flex justify-content-center align-items-center" style={{ display: 'grid' }}>
        {(!errorMessage && navigator.mediaDevices && scanning) &&
          <div className="d-flex justify-content-center">
            <video
              autoPlay
              playsInline={true}
              ref={videoRef}
              className={`w-75 h-75 ${(videoRef && videoRef.current && videoRef.current.srcObject) ? '' : 'd-none'}`}
              muted
            >
              <canvas width={videoWidth} height={videoHeight} ref={canvasRef} />
            </video>
          </div>
        }
        {errorMessage &&
          <div className="text-danger text-center">
            {Common.getBrString(errorMessage)}
          </div>
        }
        {message &&
          <div className="text-danger text-center">
            {Common.getBrString(message)}
          </div>
        }
      </div>
    </div>
  );
};

export default QRCodeScanner;